import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Briefcase, MapPin, Clock, PoundSterling, Loader2, Search, Filter, X, Building2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { jobsAPI } from '../services/api';
import { toast } from 'sonner';
import { parseErrorMessage } from '../utils/errorHandler';

const Jobs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Get current user persona
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPersona = currentUser.persona || '';
  
  // Check if user can post jobs (only professionals, not athletes or fans)
  const canPostJob = ['coach', 'nutritionist', 'physio', 'club'].includes(userPersona.toLowerCase());
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedEmploymentType, setSelectedEmploymentType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('open');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  
  // Post Job Modal states
  const [showPostJobModal, setShowPostJobModal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    jobType: '',
    location: '',
    requirements: '',
    salary: '',
    employmentType: 'Full-time',
    sport: '',
    experienceLevel: 'Intermediate',
    applicationUrl: '',
    imageUrl: '',
    image: null
  });

  // Available filter options
  const jobTypes = ['All Types', 'Coach', 'Nutritionist', 'Physiotherapist', 'Manager', 'Scout', 'Trainer', 'Analyst', 'Doctor'];
  const sports = ['All Sports', 'Football', 'Basketball', 'Track & Field', 'Swimming', 'Tennis', 'Cricket', 'Rugby', 'Volleyball'];
  const locations = ['All Locations', 'Manchester', 'London', 'Birmingham', 'Liverpool', 'Leeds', 'Barcelona', 'Madrid', 'Los Angeles', 'New York'];
  const employmentTypes = ['All Employment', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
  const statuses = [
    { label: 'Open', value: 'open' },
    { label: 'Closed', value: 'closed' },
    { label: 'All', value: 'all' }
  ];

  const fetchJobs = async (pageNum = 1, resetList = false) => {
    try {
      setLoading(true);
      
      // Build filter parameters
      const params = {
        page: pageNum,
        limit: 12,
        status: selectedStatus !== 'all' ? selectedStatus : 'open'
      };
      
      if (selectedJobType !== 'all') params.jobType = selectedJobType;
      if (selectedLocation !== 'all') params.location = selectedLocation;
      if (selectedSport !== 'all') params.sport = selectedSport;
      if (selectedEmploymentType !== 'all') params.employmentType = selectedEmploymentType;
      
      const response = await jobsAPI.getJobs(params);
      const newJobs = response.data.jobs;

      if (pageNum === 1 || resetList) {
        setJobs(newJobs);
      } else {
        setJobs((prev) => [...prev, ...newJobs]);
      }

      setHasMore(response.data.hasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(1, true);
  }, [selectedJobType, selectedLocation, selectedSport, selectedEmploymentType, selectedStatus]);

  // Check for create=true query param to open modal
  useEffect(() => {
    if (searchParams.get('create') === 'true' && canPostJob) {
      setShowPostJobModal(true);
      // Remove the query param after opening modal
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, canPostJob]);

  const handleLoadMore = () => {
    fetchJobs(page + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is done client-side for now
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedJobType('all');
    setSelectedLocation('all');
    setSelectedSport('all');
    setSelectedEmploymentType('all');
    setSelectedStatus('open');
  };

  const activeFiltersCount = () => {
    let count = 0;
    if (selectedJobType !== 'all') count++;
    if (selectedLocation !== 'all') count++;
    if (selectedSport !== 'all') count++;
    if (selectedEmploymentType !== 'all') count++;
    if (selectedStatus !== 'open') count++;
    return count;
  };

  // Filter jobs based on search query (client-side)
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = searchQuery === '' || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.jobType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  const getJobTypeImage = (jobType) => {
    const images = {
      'Coach': 'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=600',
      'Nutritionist': 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600',
      'Physiotherapist': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600',
      'Manager': 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600',
      'Trainer': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600',
    };
    return images[jobType] || 'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=600';
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!jobForm.title || !jobForm.description || !jobForm.jobType || !jobForm.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setPosting(true);
    
    try {
      // Pass the form data as an object, the API will convert it to FormData
      await jobsAPI.createJob(jobForm);
      
      toast.success('Job posted successfully!');
      setShowPostJobModal(false);
      setJobForm({
        title: '',
        description: '',
        jobType: '',
        location: '',
        requirements: '',
        salary: '',
        employmentType: 'Full-time',
        sport: '',
        experienceLevel: 'Intermediate',
        applicationUrl: '',
        imageUrl: '',
        image: null
      });
      
      // Refresh job list
      fetchJobs();
      
    } catch (error) {
      console.error('Error posting job:', error);
      const errorMessage = parseErrorMessage(error, 'Failed to post job');
      toast.error(errorMessage);
    } finally {
      setPosting(false);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 pt-20" data-testid="jobs-skeleton">
          <div className="py-6">
            <div className="mb-6">
              <Skeleton className="h-8 w-40 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-full rounded-2xl mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-32 w-full rounded-none" />
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3.5 w-28" />
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3.5 w-full" />
                    <Skeleton className="h-3.5 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-20">
        <div className="py-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Sports Jobs</h1>
                <p className="text-muted-foreground mt-1">Find and apply for sports job opportunities</p>
              </div>
              {canPostJob && (
                <Button
                  onClick={() => setShowPostJobModal(true)}
                  className="bg-primary hover:bg-primary/80 text-white rounded-xl"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Post a Job
                </Button>
              )}
            </div>

            {/* Search and Filter Section */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search jobs by title, type, company, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10 rounded-2xl"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </form>

              {/* Filter Button (Mobile) */}
              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="md:hidden relative">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {activeFiltersCount() > 0 && (
                      <Badge className="ml-2 bg-primary text-white">
                        {activeFiltersCount()}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription>
                      Filter jobs by type, location, sport, and more
                    </SheetDescription>
                  </SheetHeader>
                  <div className="space-y-6 mt-6">
                    {/* Job Type Filter */}
                    <div>
                      <Label htmlFor="jobType-mobile">Job Type</Label>
                      <Select value={selectedJobType} onValueChange={setSelectedJobType}>
                        <SelectTrigger id="jobType-mobile">
                          <SelectValue placeholder="Select job type" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobTypes.map((type) => (
                            <SelectItem key={type} value={type === 'All Types' ? 'all' : type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sport Filter */}
                    <div>
                      <Label htmlFor="sport-mobile">Sport</Label>
                      <Select value={selectedSport} onValueChange={setSelectedSport}>
                        <SelectTrigger id="sport-mobile">
                          <SelectValue placeholder="Select sport" />
                        </SelectTrigger>
                        <SelectContent>
                          {sports.map((sport) => (
                            <SelectItem key={sport} value={sport === 'All Sports' ? 'all' : sport}>
                              {sport}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Location Filter */}
                    <div>
                      <Label htmlFor="location-mobile">Location</Label>
                      <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                        <SelectTrigger id="location-mobile">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((loc) => (
                            <SelectItem key={loc} value={loc === 'All Locations' ? 'all' : loc}>
                              {loc}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Employment Type Filter */}
                    <div>
                      <Label htmlFor="employmentType-mobile">Employment Type</Label>
                      <Select value={selectedEmploymentType} onValueChange={setSelectedEmploymentType}>
                        <SelectTrigger id="employmentType-mobile">
                          <SelectValue placeholder="Select employment type" />
                        </SelectTrigger>
                        <SelectContent>
                          {employmentTypes.map((type) => (
                            <SelectItem key={type} value={type === 'All Employment' ? 'all' : type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status Filter */}
                    <div>
                      <Label htmlFor="status-mobile">Status</Label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger id="status-mobile">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {activeFiltersCount() > 0 && (
                      <Button 
                        variant="outline" 
                        onClick={clearFilters}
                        className="w-full"
                      >
                        Clear All Filters
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex gap-4 mb-6 flex-wrap items-center">
              <Select value={selectedJobType} onValueChange={setSelectedJobType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Job Type" />
                </SelectTrigger>
                <SelectContent>
                  {jobTypes.map((type) => (
                    <SelectItem key={type} value={type === 'All Types' ? 'all' : type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSport} onValueChange={setSelectedSport}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport} value={sport === 'All Sports' ? 'all' : sport}>
                      {sport}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc === 'All Locations' ? 'all' : loc}>
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedEmploymentType} onValueChange={setSelectedEmploymentType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Employment Type" />
                </SelectTrigger>
                <SelectContent>
                  {employmentTypes.map((type) => (
                    <SelectItem key={type} value={type === 'All Employment' ? 'all' : type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {activeFiltersCount() > 0 && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-sm"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear ({activeFiltersCount()})
                </Button>
              )}
            </div>
          </div>

          {/* Jobs Grid */}
          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No jobs found</h3>
              <p className="text-muted-foreground">
                {searchQuery || activeFiltersCount() > 0
                  ? 'Try adjusting your search or filters'
                  : 'Check back later for new opportunities'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredJobs.map((job) => (
                  <Card 
                    key={job._id} 
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/jobs/${job._id}`)}
                  >
                    <div 
                      className="h-32 bg-cover bg-center"
                      style={{ 
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${getJobTypeImage(job.jobType)})` 
                      }}
                    />
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge 
                          variant={job.status === 'open' ? 'default' : 'secondary'}
                          className={job.status === 'open' ? 'btn-primary' : ''}
                        >
                          {job.status}
                        </Badge>
                        {job.employmentType && (
                          <Badge variant="outline">
                            {job.employmentType}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                        {job.title}
                      </h3>
                      
                      {job.company && (
                        <div className="flex items-center text-sm text-muted-foreground mb-2">
                          <Building2 className="w-4 h-4 mr-1" />
                          {job.company}
                        </div>
                      )}
                      
                      <div className="flex items-center text-sm text-muted-foreground mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        {job.location}
                      </div>
                      
                      {job.jobType && (
                        <div className="flex items-center text-sm text-muted-foreground mb-2">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {job.jobType}
                        </div>
                      )}
                      
                      {job.salary && (
                        <div className="flex items-center text-sm text-muted-foreground mb-2">
                          <PoundSterling className="w-4 h-4 mr-1" />
                          {job.salary}
                        </div>
                      )}
                      
                      {job.applicationUrl && (
                        <div className="flex items-center text-sm mb-2">
                          <a
                            href={job.applicationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-primary hover:text-primary/80 transition-colors font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Apply Now
                          </a>
                        </div>
                      )}
                      
                      {job.deadline && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Clock className="w-4 h-4 mr-1" />
                          Apply by: {new Date(job.deadline).toLocaleDateString()}
                        </div>
                      )}
                      
                      {job.description && (
                        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                          {job.description}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-8">
                  <Button
                    onClick={handleLoadMore}
                    disabled={loading}
                    variant="outline"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* Post Job Modal */}
      {showPostJobModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border-0">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
              <h2 className="text-lg font-semibold text-foreground">Post a Job</h2>
              <button
                onClick={() => setShowPostJobModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handlePostJob} className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                {/* Job Title */}
                <div>
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={jobForm.title}
                    onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                    placeholder="e.g., Football Coach, Nutritionist"
                    required
                  />
                </div>

                {/* Job Type */}
                <div>
                  <Label htmlFor="jobType">Job Type *</Label>
                  <Select
                    value={jobForm.jobType}
                    onValueChange={(value) => setJobForm({...jobForm, jobType: value})}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select job type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Coach">Coach</SelectItem>
                      <SelectItem value="Nutritionist">Nutritionist</SelectItem>
                      <SelectItem value="Physiotherapist">Physiotherapist</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Scout">Scout</SelectItem>
                      <SelectItem value="Trainer">Trainer</SelectItem>
                      <SelectItem value="Analyst">Analyst</SelectItem>
                      <SelectItem value="Doctor">Doctor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Sport */}
                <div>
                  <Label htmlFor="sport">Sport</Label>
                  <Select
                    value={jobForm.sport}
                    onValueChange={(value) => setJobForm({...jobForm, sport: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Football">Football</SelectItem>
                      <SelectItem value="Basketball">Basketball</SelectItem>
                      <SelectItem value="Tennis">Tennis</SelectItem>
                      <SelectItem value="Cricket">Cricket</SelectItem>
                      <SelectItem value="Rugby">Rugby</SelectItem>
                      <SelectItem value="Swimming">Swimming</SelectItem>
                      <SelectItem value="Track & Field">Track & Field</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={jobForm.location}
                    onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                    placeholder="e.g., London, Manchester"
                    required
                  />
                </div>

                {/* Employment Type */}
                <div>
                  <Label htmlFor="employmentType">Employment Type</Label>
                  <Select
                    value={jobForm.employmentType}
                    onValueChange={(value) => setJobForm({...jobForm, employmentType: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Freelance">Freelance</SelectItem>
                      <SelectItem value="Internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Experience Level */}
                <div>
                  <Label htmlFor="experienceLevel">Experience Level</Label>
                  <Select
                    value={jobForm.experienceLevel}
                    onValueChange={(value) => setJobForm({...jobForm, experienceLevel: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Entry">Entry Level</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Senior">Senior</SelectItem>
                      <SelectItem value="Expert">Expert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Salary */}
                <div>
                  <Label htmlFor="salary">Salary (Optional)</Label>
                  <Input
                    id="salary"
                    value={jobForm.salary}
                    onChange={(e) => setJobForm({...jobForm, salary: e.target.value})}
                    placeholder="e.g., £30,000 - £40,000"
                  />
                </div>

                {/* Application URL */}
                <div>
                  <Label htmlFor="applicationUrl">Application Link (Optional)</Label>
                  <Input
                    id="applicationUrl"
                    type="url"
                    value={jobForm.applicationUrl}
                    onChange={(e) => setJobForm({...jobForm, applicationUrl: e.target.value})}
                    placeholder="e.g., https://example.com/apply or email@company.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Add an external application link or email address
                  </p>
                </div>

                {/* Image Upload */}
                <div>
                  <Label htmlFor="image">Job Image (Optional)</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setJobForm({...jobForm, image: file});
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload a job poster or company image (JPG, PNG, max 5MB)
                  </p>
                  {jobForm.image && (
                    <div className="mt-2 text-sm text-primary">
                      Selected: {jobForm.image.name}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Job Description *</Label>
                  <textarea
                    id="description"
                    value={jobForm.description}
                    onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                    placeholder="Describe the role, responsibilities, and what you're looking for..."
                    className="w-full min-h-[120px] px-3 py-2 text-sm rounded-xl border border-border bg-background"
                    required
                  />
                </div>

                {/* Requirements */}
                <div>
                  <Label htmlFor="requirements">Requirements (Optional)</Label>
                  <textarea
                    id="requirements"
                    value={jobForm.requirements}
                    onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})}
                    placeholder="List the skills, qualifications, and experience required..."
                    className="w-full min-h-[100px] px-3 py-2 text-sm rounded-xl border border-border bg-background"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-border/50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPostJobModal(false)}
                  disabled={posting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/80 text-white"
                  disabled={posting}
                >
                  {posting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Job'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
